import { Category, ConstraintRule, ConstraintViolation, EventItem, RuleConfig } from './types.ts';
import { calculateTimeSlotAnalysis } from './timeSlots.ts';
import { calculateEventFinancials } from './financials.ts';


export const DEFAULT_CATEGORY_MAX_RATIOS: Record<Category, number> = {
  Drinking: 10,
  Trip: 10,
  Sports: 15,
  Social: 20,
  Culture: 25,
  Art: 25,
  Academic: 30,
  Other: 20,
};

export const DEFAULT_RULE_CONFIG: RuleConfig = {
  maxConcurrentEvents: 2,
  minCultureEventsPerWeek: 2,
  maxOrganisersPerTimeSlot: 10,
  warnOnDeficit: true,
  maxParticipantsPerOrganiserDefault: 20,
  categoryMaxRatioThresholds: DEFAULT_CATEGORY_MAX_RATIOS,
};


/**
 * Hard Constraint 1: No more than N events at the same time.
 * Evaluates overlapping time windows across all events.
 */
export const maxConcurrentEventsRule: ConstraintRule = {
  id: 'max-concurrent-events',
  name: 'Maximum Concurrent Events',
  description: 'Hard limit on how many events can occur simultaneously (default max: 2).',
  severity: 'hard',
  category: 'Concurrency',
  check: (events: EventItem[], config: RuleConfig): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    const limit = config.maxConcurrentEvents ?? 2;

    if (events.length <= limit) return violations;

    // Build timeline points (start +1, end -1)
    interface Point {
      time: number;
      type: 'start' | 'end';
      eventId: string;
      title: string;
    }

    const points: Point[] = [];
    for (const ev of events) {
      const startTime = new Date(ev.start).getTime();
      const endTime = new Date(ev.end).getTime();
      if (!isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
        points.push({ time: startTime, type: 'start', eventId: ev.id, title: ev.title });
        points.push({ time: endTime, type: 'end', eventId: ev.id, title: ev.title });
      }
    }

    // Sort timeline points
    points.sort((a, b) => {
      if (a.time !== b.time) return a.time - b.time;
      // 'end' points processed before 'start' points at exact same timestamp
      return a.type === 'end' ? -1 : 1;
    });

    const activeEventIds = new Set<string>();
    const reportedSets = new Set<string>();

    for (const pt of points) {
      if (pt.type === 'start') {
        activeEventIds.add(pt.eventId);
        if (activeEventIds.size > limit) {
          const ids = Array.from(activeEventIds).sort();
          const key = ids.join('|');
          if (!reportedSets.has(key)) {
            reportedSets.add(key);
            const overlappingTitles = events
              .filter(e => ids.includes(e.id))
              .map(e => `"${e.title}"`)
              .join(', ');

            violations.push({
              ruleId: 'max-concurrent-events',
              ruleName: 'Maximum Concurrent Events Exceeded',
              severity: 'hard',
              message: `Too many overlapping events (${activeEventIds.size} active, max allowed is ${limit}).`,
              affectedEventIds: ids,
              details: `Conflicting events: ${overlappingTitles}`,
            });
          }
        }
      } else {
        activeEventIds.delete(pt.eventId);
      }
    }

    return violations;
  },
};

/**
 * Hard Constraint 2: Event start must be before end time.
 */
export const validTimeWindowRule: ConstraintRule = {
  id: 'valid-time-window',
  name: 'Valid Event Duration',
  description: 'Event start time must be before its end time.',
  severity: 'hard',
  category: 'Schedule',
  check: (events: EventItem[]): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    for (const ev of events) {
      const startTime = new Date(ev.start).getTime();
      const endTime = new Date(ev.end).getTime();
      if (isNaN(startTime) || isNaN(endTime) || startTime >= endTime) {
        violations.push({
          ruleId: 'valid-time-window',
          ruleName: 'Invalid Event Duration',
          severity: 'hard',
          message: `Event "${ev.title}" has an invalid duration (start must be before end).`,
          affectedEventIds: [ev.id],
        });
      }
    }
    return violations;
  },
};

/**
 * Global Soft Constraint 1: Minimum number of Culture events per week.
 * Week 1: 2026-09-28 to 2026-10-04
 * Week 2: 2026-10-05 to 2026-10-11
 */
export const minCulturePerWeekRule: ConstraintRule = {
  id: 'min-culture-events-per-week',
  name: 'Minimum Culture Events per Week',
  description: 'Global target requiring at least N Culture events per week (default min: 2).',
  severity: 'soft',
  category: 'Global',
  check: (events: EventItem[], config: RuleConfig): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    const minCulture = config.minCultureEventsPerWeek ?? 2;

    const week1Start = new Date('2026-09-28T00:00:00');
    const week1End = new Date('2026-10-04T23:59:59');
    const week2Start = new Date('2026-10-05T00:00:00');
    const week2End = new Date('2026-10-11T23:59:59');

    let week1CultureCount = 0;
    let week2CultureCount = 0;
    const week1EventIds: string[] = [];
    const week2EventIds: string[] = [];

    for (const ev of events) {
      if (ev.category === 'Culture') {
        const evStart = new Date(ev.start);
        if (evStart >= week1Start && evStart <= week1End) {
          week1CultureCount++;
          week1EventIds.push(ev.id);
        } else if (evStart >= week2Start && evStart <= week2End) {
          week2CultureCount++;
          week2EventIds.push(ev.id);
        }
      }
    }

    if (week1CultureCount < minCulture) {
      violations.push({
        ruleId: 'min-culture-events-per-week',
        ruleName: 'Low Culture Events (Week 1)',
        severity: 'soft',
        message: `Week 1 (Sept 28 - Oct 04) has only ${week1CultureCount} Culture event(s). Target is at least ${minCulture}.`,
        affectedEventIds: week1EventIds,
        details: 'Consider adding more cultural activities or re-categorizing appropriate events.',
      });
    }

    if (week2CultureCount < minCulture) {
      violations.push({
        ruleId: 'min-culture-events-per-week',
        ruleName: 'Low Culture Events (Week 2)',
        severity: 'soft',
        message: `Week 2 (Oct 05 - Oct 11) has only ${week2CultureCount} Culture event(s). Target is at least ${minCulture}.`,
        affectedEventIds: week2EventIds,
        details: 'Consider adding more cultural activities in the second week of O-Weeks.',
      });
    }

    return violations;
  },
};

/**
 * Soft Constraint 2: Organiser Workload Capacity Check.
 * Warns if total required organisers for concurrent events exceeds max threshold.
 */
export const organiserWorkloadRule: ConstraintRule = {
  id: 'organiser-workload',
  name: 'Organiser Staffing Threshold',
  description: 'Soft warning if concurrent events require more total organisers than available limit.',
  severity: 'soft',
  category: 'Capacity',
  check: (events: EventItem[], config: RuleConfig): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    const maxOrg = config.maxOrganisersPerTimeSlot ?? 10;

    interface Point {
      time: number;
      type: 'start' | 'end';
      ev: EventItem;
    }

    const points: Point[] = [];
    for (const ev of events) {
      const startTime = new Date(ev.start).getTime();
      const endTime = new Date(ev.end).getTime();
      if (!isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
        points.push({ time: startTime, type: 'start', ev });
        points.push({ time: endTime, type: 'end', ev });
      }
    }

    points.sort((a, b) => (a.time !== b.time ? a.time - b.time : a.type === 'end' ? -1 : 1));

    const activeMap = new Map<string, EventItem>();
    const reportedKeys = new Set<string>();

    for (const pt of points) {
      if (pt.type === 'start') {
        activeMap.set(pt.ev.id, pt.ev);
        let totalOrgsNeeded = 0;
        activeMap.forEach(e => { totalOrgsNeeded += e.organisers || 0; });

        if (totalOrgsNeeded > maxOrg) {
          const ids = Array.from(activeMap.keys()).sort();
          const key = ids.join('|');
          if (!reportedKeys.has(key)) {
            reportedKeys.add(key);
            violations.push({
              ruleId: 'organiser-workload',
              ruleName: 'High Organiser Demand',
              severity: 'soft',
              message: `Concurrent events require ${totalOrgsNeeded} organisers total, exceeding recommended limit of ${maxOrg}.`,
              affectedEventIds: ids,
              details: `Events involved: ${Array.from(activeMap.values()).map(e => `${e.title} (${e.organisers} orgs)`).join(', ')}`,
            });
          }
        }
      } else {
        activeMap.delete(pt.ev.id);
      }
    }

    return violations;
  },
};

/**
 * Soft Constraint 3: Financial Deficit & STIBET Compliance Warning.
 * Warns when an event leaves an unrecovered deficit for the section or violates fee guidelines.
 */
export const financialDeficitRule: ConstraintRule = {
  id: 'financial-deficit',
  name: 'Financial Deficit & Pricing Warning',
  description: 'Soft warning for events where total projected costs exceed STIBET grants, external funding, and net participant revenue.',
  severity: 'soft',
  category: 'Financial',
  check: (events: EventItem[], config: RuleConfig): ConstraintViolation[] => {
    if (!config.warnOnDeficit) return [];
    const violations: ConstraintViolation[] = [];

    for (const ev of events) {
      const details = calculateEventFinancials(ev);

      if (details.sectionNetBalance < -0.01) {
        const deficit = Math.abs(details.sectionNetBalance);
        violations.push({
          ruleId: 'financial-deficit',
          ruleName: 'Projected Section Deficit',
          severity: 'soft',
          message: `Event "${ev.title}" (${details.fundingModel}) has an unrecovered deficit of €${deficit.toFixed(2)}.`,
          affectedEventIds: [ev.id],
          details: `Total Cost: €${details.totalCost.toFixed(2)} | STIBET Funding: €${details.appliedStibetFunding.toFixed(2)} | Net Ticket Rev: €${details.netRevenueAfterAppFeeAndTax.toFixed(2)}. Recommended min ticket price to break even: €${details.recommendedMinPricePerParticipant.toFixed(2)} per person.`,
        });
      }
    }

    return violations;
  },
};


/**
 * Soft Constraint 4: Participant-to-Organiser Ratio Threshold.
 * Warns if an event's spots-per-organiser ratio exceeds category safety thresholds.
 * Certain categories (e.g. Trip, Drinking, Sports) require more organisers per participant,
 * while others (e.g. Academic, Culture, Art) need fewer organisers per participant.
 */
export const participantOrganiserRatioRule: ConstraintRule = {
  id: 'participant-organiser-ratio',
  name: 'Participant-to-Organiser Ratio Threshold',
  description: 'Soft warning if participant capacity per organiser exceeds category safety threshold.',
  severity: 'soft',
  category: 'Capacity',
  check: (events: EventItem[], config: RuleConfig): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    const defaultMax = config.maxParticipantsPerOrganiserDefault ?? 20;

    for (const ev of events) {
      const catThreshold = config.categoryMaxRatioThresholds?.[ev.category]
        ?? DEFAULT_CATEGORY_MAX_RATIOS[ev.category]
        ?? defaultMax;

      const capacity = Number(ev.capacity) || 0;
      const organisers = Number(ev.organisers) || 0;

      if (capacity > 0 && organisers === 0) {
        violations.push({
          ruleId: 'participant-organiser-ratio',
          ruleName: 'Unstaffed Event Warning',
          severity: 'soft',
          message: `Event "${ev.title}" (${ev.category}) has ${capacity} spots but 0 organisers assigned!`,
          affectedEventIds: [ev.id],
          details: `Category "${ev.category}" events require at least 1 organiser. Target ratio threshold is ≤${catThreshold} spots per organiser.`,
        });
      } else if (organisers > 0) {
        const ratio = capacity / organisers;
        if (ratio > catThreshold) {
          violations.push({
            ruleId: 'participant-organiser-ratio',
            ruleName: 'High Participant:Organiser Ratio',
            severity: 'soft',
            message: `Event "${ev.title}" has a ratio of ${ratio.toFixed(1)} spots/org (${capacity} spots / ${organisers} orgs), exceeding the threshold of ${catThreshold} for ${ev.category}.`,
            affectedEventIds: [ev.id],
            details: `Category "${ev.category}" threshold is ${catThreshold} spots/organiser. Consider adding organisers or reducing capacity.`,
          });
        }
      }
    }

    return violations;
  },
};

/**
 * Soft Constraint 5: Prime-Time Slot Coverage Check (Noon, Afternoon, Evening).
 * Evaluates whether active days in O-Weeks have events scheduled across Noon, Afternoon, and Evening slots.
 * Morning events are ignored for this fill optimization metric.
 */
export const primeTimeCoverageRule: ConstraintRule = {
  id: 'prime-time-coverage',
  name: 'Prime-Time Slot Coverage',
  description: 'Soft warning if an active schedule day is missing events in Noon, Afternoon, or Evening slots.',
  severity: 'soft',
  category: 'Schedule',
  check: (events: EventItem[], _config: RuleConfig): ConstraintViolation[] => {
    const violations: ConstraintViolation[] = [];
    const { daysSummary } = calculateTimeSlotAnalysis(events);

    for (const daySummary of daysSummary) {
      // Audit days that already have at least 1 event scheduled
      const totalEventsOnDay = Object.values(daySummary.slots).reduce((acc, s) => acc + s.count, 0);
      if (totalEventsOnDay === 0) continue;

      if (daySummary.missingPrimeSlots.length > 0) {
        const dayEvents = events.filter(e => e.start && e.start.startsWith(daySummary.dateStr));
        const dayEventIds = dayEvents.map(e => e.id);

        violations.push({
          ruleId: 'prime-time-coverage',
          ruleName: 'Incomplete Prime-Time Coverage',
          severity: 'soft',
          message: `${daySummary.formattedDate} (${daySummary.dateStr}) is missing prime-time events in: ${daySummary.missingPrimeSlots.join(', ')}.`,
          affectedEventIds: dayEventIds,
          details: `Prime-time slots (Noon, Afternoon, Evening) should be filled to maximize student participation. Morning events are ignored for this target.`,
        });
      }
    }

    return violations;
  },
};

// All available constraint rules in the system
export const ALL_RULES: ConstraintRule[] = [
  maxConcurrentEventsRule,
  validTimeWindowRule,
  minCulturePerWeekRule,
  organiserWorkloadRule,
  financialDeficitRule,
  participantOrganiserRatioRule,
  primeTimeCoverageRule,
];



/**
 * Engine function: runs enabled rules against event list.
 */
export function runLinting(events: EventItem[], config: RuleConfig = DEFAULT_RULE_CONFIG): ConstraintViolation[] {
  let violations: ConstraintViolation[] = [];
  for (const rule of ALL_RULES) {
    try {
      const res = rule.check(events, config);
      violations = violations.concat(res);
    } catch (err) {
      console.error(`Error executing rule ${rule.id}:`, err);
    }
  }
  return violations;
}

/**
 * Helper to quickly get map of eventId -> array of violations affecting that event.
 */
export function getViolationsByEvent(violations: ConstraintViolation[]): Map<string, ConstraintViolation[]> {
  const map = new Map<string, ConstraintViolation[]>();
  for (const v of violations) {
    for (const id of v.affectedEventIds) {
      const list = map.get(id) || [];
      list.push(v);
      map.set(id, list);
    }
  }
  return map;
}
